import { useState } from "react";
import type { ShipmentDetail } from "api";
import cn from "classnames";
import type { LoadStatus } from "hooks/useProfile";
import { useScrollThumb } from "hooks/useScrollThumb";
import type { useShipmentDocuments } from "hooks/useShipmentDocuments";
import { PlusIcon, UploadIcon } from "./docs.icons";
import type { DocSegment } from "./docs.model";
import { containerDocGroups, DOC_SEGMENTS, documentsSub, shipmentLevelDocuments } from "./docs.model";
import { DocumentItem } from "./DocumentItem";
import { useDocumentActions } from "./useDocumentActions";

type DocumentsTabProps = {
  shipment: ShipmentDetail;
  docs: ReturnType<typeof useShipmentDocuments>;
};

const StateNote = ({ status, onRetry }: { status: LoadStatus; onRetry: () => void }) => {
  if (status === "loading") return <span className="pc-spin" aria-label="Loading" />;
  if (status === "unverified") return <p className="pc-note">Verify your email address to see documents.</p>;

  return (
    <div className="shipp__state-msg">
      <p className="pc-note pc-note--bad">Could not load the documents.</p>
      <button type="button" className="pcbtn" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
};

/**
 * The Documents tab: the shipment's own files, then one collapsible group per
 * container, filtered by the design's All / Shipment / By container control.
 */
export const DocumentsTab = ({ shipment, docs }: DocumentsTabProps) => {
  const [segment, setSegment] = useState<DocSegment>("all");
  /** Groups the visitor opened or closed; the rest follow "open when it has files". */
  const [toggled, setToggled] = useState<Record<number, boolean>>({});
  const { listRef, thumbRef } = useScrollThumb();
  const actions = useDocumentActions(docs);

  const shipmentDocs = shipmentLevelDocuments(docs.documents);
  const groups = containerDocGroups(shipment, docs.documents);

  return (
    <>
      <div className="shdoc__top shdoc__top--tab">
        <div className="shdoc__ttl">
          <div className="shdoc__h">Documents</div>
          <p className="shdoc__sub">{documentsSub(shipment)}</p>
        </div>
        <button
          type="button"
          className="pcbtn shdoc__up"
          disabled={docs.status !== "ready" || actions.uploading}
          onClick={() => actions.chooseFile(null)}
        >
          <UploadIcon />
          {actions.uploading ? "Uploading…" : "Upload document"}
        </button>
      </div>

      {actions.error !== null && <p className="pc-note pc-note--bad shdoc__err">{actions.error}</p>}

      <div className="shdoc__seg" role="tablist">
        {DOC_SEGMENTS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={segment === item.key}
            className={cn("shdoc__sg", { "is-on": segment === item.key })}
            onClick={() => setSegment(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="shdoc__wrap">
        <div className="shdoc__list shdoc__list--tab" ref={listRef}>
          {docs.status !== "ready" ? (
            <StateNote status={docs.status} onRetry={docs.retry} />
          ) : (
            <>
              {segment !== "ct" && (
                <section className="shdoc__grp">
                  <div className="shdoc__gh">
                    <span className="shdoc__gt">Shipment documents</span>
                    <b className="shdoc__gn">{shipmentDocs.length}</b>
                  </div>
                  <div className="shdoc__gb">
                    {shipmentDocs.length === 0 && <div className="shdoc__none">No documents yet</div>}
                    {shipmentDocs.map((document) => (
                      <DocumentItem key={document.id} document={document} onDelete={actions.askDelete} />
                    ))}
                  </div>
                </section>
              )}

              {segment !== "ship" &&
                groups.map(({ container, label, documents }) => {
                  const open = toggled[container.id] ?? documents.length > 0;

                  return (
                    <section key={container.id} className={cn("shdoc__grp", { "is-off": !open })}>
                      {/* The design makes the whole header one button with Upload inside
                          it. Buttons cannot nest, so the toggle is stretched over the
                          header and Upload sits above it. */}
                      <div className="shdoc__gh shdoc__gh--btn">
                        <button
                          type="button"
                          className="shdoc__gtog"
                          aria-expanded={open}
                          aria-label={label}
                          onClick={() => setToggled((current) => ({ ...current, [container.id]: !open }))}
                        />
                        <i className="shdoc__gc" />
                        <span className="shdoc__gt">{label}</span>
                        <b className="shdoc__gn">{documents.length}</b>
                        <button
                          type="button"
                          className="shdoc__gup"
                          disabled={actions.uploading}
                          onClick={() => {
                            setToggled((current) => ({ ...current, [container.id]: true }));
                            actions.chooseFile(container.id);
                          }}
                        >
                          <PlusIcon />
                          Upload
                        </button>
                      </div>
                      <div className="shdoc__gb">
                        {documents.length === 0 && <div className="shdoc__none">No documents yet</div>}
                        {documents.map((document) => (
                          <DocumentItem key={document.id} document={document} onDelete={actions.askDelete} />
                        ))}
                      </div>
                    </section>
                  );
                })}

              {segment !== "ship" && groups.length === 0 && (
                <div className="shdoc__none shdoc__none--pad">Add containers to attach documents to them.</div>
              )}
            </>
          )}
        </div>
        <div className="shdoc__thumb" ref={thumbRef} />
      </div>

      {actions.elements}
    </>
  );
};
